import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@mantine/core';
import { SectionCard, SectionForm, useSections } from '@modules/sections';
import { useCategoriesWithoutSections } from '@modules/categories';
import { Breadcrumbs } from '@components';
import { Modal, RefetchButton } from '@shared/ui';
import SectionCategoriesPage from '../SectionCategoriesPage';
import '@shared/styles/page.css';
import './SectionsPage.css';
import { IoAdd } from 'react-icons/io5';

type SectionSelection = 'popular' | 'without' | number;

function selectionFromRoute(pathname: string, sectionId?: string): SectionSelection {
  if (pathname.startsWith('/sections/popular')) return 'popular';
  if (pathname.startsWith('/sections/without-sections')) return 'without';
  const id = sectionId ? Number(sectionId) : NaN;
  if (Number.isInteger(id) && id > 0) return id;
  return 'popular';
}

export default function SectionsPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
  const { data: sections = [], isLoading, error, refetch } = useSections();
  const { data: categoriesWithoutSections = [] } = useCategoriesWithoutSections();
  const location = useLocation();
  const navigate = useNavigate();
  const { sectionId } = useParams<{ sectionId?: string }>();
  const selected = selectionFromRoute(location.pathname, sectionId);

  useEffect(() => {
    if (location.pathname === '/sections') {
      navigate('/sections/popular', { replace: true });
    }
  }, [location.pathname, navigate]);

  const list = (
    <aside className="sections-workspace__list">
      <Link
        to="/sections/popular"
        className={`sections-nav__card${selected === 'popular' ? ' is-selected' : ''}`}
      >
        <span className="sections-nav__thumb" aria-hidden>🔥</span>
        <span className="sections-nav__name">Популярные</span>
      </Link>
      {categoriesWithoutSections.length > 0 ? (
        <Link
          to="/sections/without-sections"
          className={`sections-nav__card${selected === 'without' ? ' is-selected' : ''}`}
        >
          <span className="sections-nav__thumb" aria-hidden>📁</span>
          <span className="sections-nav__name">Без секции</span>
        </Link>
      ) : null}
      {sections.map((section) => (
        <SectionCard
          key={section.id}
          section={section}
          compact
          selected={selected === section.id}
          onEdit={() => setEditingSectionId(section.id)}
        />
      ))}
      <button
        type="button"
        className="sections-nav__card sections-nav__card--add"
        onClick={() => setIsCreateModalOpen(true)}
      >
        <IoAdd size={18} aria-hidden />
        <span className="sections-nav__name">Секция</span>
      </button>
    </aside>
  );

  return (
    <div className="page-container">
      <Breadcrumbs items={[
        { label: 'Главная', path: '/' },
        { label: 'Категории' },
      ]} />

      <div className="page-header">
        <h1>Категории</h1>
      </div>

      {isLoading ? (
        <div className="content-wrapper content-wrapper--centered">
          <div className="empty-container">
            <Loader />
          </div>
        </div>
      ) : error && refetch ? (
        <div className="content-wrapper">
          <RefetchButton refetch={refetch} isLoading={isLoading} />
        </div>
      ) : (
        <div className="sections-workspace">
          {list}
          <div className="sections-workspace__detail">
            {location.pathname === '/sections' ? null : <SectionCategoriesPage embedded />}
          </div>
        </div>
      )}

      <Modal
        isOpen={isCreateModalOpen || editingSectionId !== null}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingSectionId(null);
        }}
        title={editingSectionId ? 'Редактировать секцию' : 'Создать секцию'}
      >
        <SectionForm
          section={editingSectionId ? sections.find((s) => s.id === editingSectionId) : undefined}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            setEditingSectionId(null);
          }}
        />
      </Modal>
    </div>
  );
}
